import React from "react";
import { PageHero, ClosingCTA } from "../components/site/Shared";
import { Reveal, FadeIn } from "../components/site/Reveal";
import { IMAGES, VALUES } from "../data/content";

const Block = ({ eyebrow, title, children, className = "" }) => (
  <div className={"grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 " + className}>
    <div className="lg:col-span-4">
      <Reveal>
        <p className="eyebrow text-brass mb-6">{eyebrow}</p>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.12] tracking-[-0.02em] text-ink">
          {title}
        </h2>
      </Reveal>
    </div>
    <div className="lg:col-span-7 lg:col-start-6">
      <Reveal delay={0.1}>
        <div className="space-y-6 text-lg text-ink/70 font-light leading-relaxed">{children}</div>
      </Reveal>
    </div>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Yadid"
        titleLines={["Trusted judgment begins", "with trusted relationship."]}
        lede="Yadid was built around a simple conviction: the leaders making consequential decisions need more than expertise around them. They need people whose judgment they trust."
      />

      <section className="yadid-container pb-8">
        <FadeIn>
          <div className="relative h-[300px] md:h-[520px] overflow-hidden">
            <img
              src={IMAGES.tableMeeting}
              alt="Leaders gathered around a table in thoughtful exchange"
              className="w-full h-full object-cover img-treated"
            />
          </div>
        </FadeIn>
      </section>

      <section className="bg-ivory">
        <div className="yadid-container py-20 md:py-28 space-y-24 md:space-y-32">
          <Block eyebrow="Why Yadid Exists" title="Leadership can become lonely when the stakes are highest.">
            <p>
              The more consequential the decision, the fewer people a leader may feel free to speak
              with candidly. Yadid exists to provide another kind of advisory relationship — one
              grounded in trust, judgment, discretion, relational intelligence, and long-term
              stewardship.
            </p>
            <p>
              We seek to understand before advising. To tell the truth with care. To recognize the
              relational dimensions of strategy. And to remain close enough to help leaders move from
              insight to action.
            </p>
          </Block>
        </div>
      </section>

      {/* The Name — large editorial blockquote */}
      <section className="bg-ink text-ivory">
        <div className="yadid-container py-24 md:py-40">
          <Reveal>
            <p className="eyebrow text-brass mb-10">The Name</p>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="font-serif text-3xl md:text-5xl lg:text-[3.75rem] leading-[1.15] tracking-[-0.02em] text-ivory max-w-5xl italic">
              Yadid means &ldquo;beloved friend.&rdquo;
            </blockquote>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-ivory/70 font-light leading-relaxed text-lg">
              A beloved friend is close enough to understand. Independent enough to tell the truth.
              Loyal enough to remain present. And trusted enough to be invited into moments that
              cannot be entrusted to everyone. That is the posture behind Yadid Advisory.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="yadid-container py-20 md:py-28 space-y-24 md:space-y-32">
          <Block eyebrow="The Trusted Advisor" title="Influence earned through trust.">
            <p>
              Yadid does not seek influence through volume, visibility, or performance. We earn it by
              listening carefully, preparing deeply, protecting confidence, understanding people and
              institutions, and exercising sound judgment.
            </p>
            <p>A trusted advisor does more than offer recommendations. A trusted advisor helps a leader see.</p>
          </Block>

          <Block eyebrow="Relationships" title="Relationships are not transactions.">
            <p>
              Relationships are shaped by history, credibility, memory, culture, expectation, trust,
              and time. Yadid helps leaders understand those dynamics and navigate them responsibly.
            </p>
            <p>
              We believe trusted access is not about merely securing an introduction. It is about
              creating credible proximity and stewarding the relationship that follows.
            </p>
          </Block>
        </div>
      </section>

      {/* Values */}
      <section className="bg-stone-soft/40 border-y border-stone">
        <div className="yadid-container py-24 md:py-36">
          <Reveal>
            <p className="eyebrow text-brass mb-6">What Guides Us</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-ink max-w-2xl">
              Ten commitments beneath the work.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-stone">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 2) * 0.06} className="bg-ivory">
                <div className="p-8 md:p-10 flex gap-6 items-baseline h-full">
                  <span className="font-serif text-brass text-lg w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{v.title}</h3>
                    <p className="mt-2 text-ink/60 font-light leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing standard */}
      <section className="bg-ivory">
        <div className="yadid-container py-24 md:py-36">
          <Reveal>
            <p className="eyebrow text-brass mb-10">The Standard</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.2] tracking-[-0.02em] text-ink max-w-4xl">
              We want every leader who works with Yadid to feel:
            </h2>
          </Reveal>
          <ul className="mt-12 max-w-3xl space-y-6">
            {[
              "They understand what is really at stake.",
              "They will tell me the truth.",
              "They understand the people around this decision.",
              "I trust their judgment.",
            ].map((line, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li className="flex gap-5 items-baseline border-b border-stone pb-6">
                  <span className="text-brass font-serif text-xl">&mdash;</span>
                  <span className="font-serif text-2xl md:text-3xl text-ink/90 italic">{line}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
